import React from "react";
import { Link } from "react-router-dom";
import ServicePageShell, {
  CardGrid,
  FaqList,
  RelatedServices,
  SectionWrap,
  Timeline,
  buildServiceSchemas,
} from "../../components/ServicePageShell";
import ServiceInquiryForm from "../../components/ServiceInquiryForm";
import hero from "../../assets/img/Q.webp";
import techStackImg from "../../assets/img/tech-stack.png";
import servicesBreakdownImg from "../../assets/img/services-breakdown.png";
import developmentProcessImg from "../../assets/img/development-process.png";

const pagePath = "/services/desktop-software";

const faqs = [
  {
    question: "What type of desktop software do you build?",
    answer:
      "We build Windows desktop applications, internal business tools, offline-first utilities, reporting tools, and desktop apps that connect with APIs, databases, and hardware where needed.",
  },
  {
    question: "Can you modernize an old desktop application?",
    answer:
      "Yes. We can redesign older desktop software, improve performance, rebuild outdated modules, and connect the application with modern cloud or web systems.",
  },
  {
    question: "Can desktop software work offline?",
    answer:
      "Yes. We can design desktop apps with local storage, offline workflows, and sync logic so teams can keep working even when internet access is unreliable.",
  },
  {
    question: "Do you provide installers and deployment support?",
    answer:
      "Yes. We can prepare installers, update flows, basic documentation, and release support so the software is easier to install and maintain across machines.",
  },
  {
    question: "Can it connect with our existing ERP or website?",
    answer:
      "Yes. Desktop software can connect with ERPs, CRMs, web apps, APIs, local databases, barcode systems, printers, and other operational tools.",
  },
];

const schemas = buildServiceSchemas({
  name: "Desktop Software Development",
  description:
    "QBITS9 builds custom desktop software for businesses in India, including Windows applications, offline tools, internal systems, integrations, and legacy app modernization.",
  path: pagePath,
  serviceType: "custom desktop software development company India",
  faqs,
});

const heroStats = [
  {
    title: "Offline-ready",
    value: "Desktop tools for teams that need dependable local workflows",
  },
  {
    title: "Business-focused",
    value: "Built around operations, reporting, integrations, and daily use",
  },
  {
    title: "Maintainable",
    value: "Structured for updates, support, and long-term ownership",
  },
];

const services = [
  {
    badge: "WIN",
    title: "Windows Desktop Apps",
    description:
      "Custom desktop applications for admin work, reporting, production tracking, billing, inventory, and internal operations.",
  },
  {
    badge: "OFF",
    title: "Offline Tools",
    description:
      "Offline-first software with local storage, data entry flows, and controlled sync for teams that cannot rely on constant connectivity.",
  },
  {
    badge: "INT",
    title: "System Integrations",
    description:
      "Desktop apps that connect with ERPs, websites, APIs, printers, barcode scanners, local databases, and third-party business tools.",
  },
  {
    badge: "MOD",
    title: "Legacy Modernization",
    description:
      "Improve outdated desktop software with cleaner interfaces, better performance, safer data handling, and maintainable architecture.",
  },
];

const useCases = [
  {
    badge: "01",
    title: "Factory and inventory control",
    description:
      "Track stock, jobs, dispatches, approvals, and production activity from a desktop system built for the way your team actually works.",
  },
  {
    badge: "02",
    title: "Billing and reporting utilities",
    description:
      "Create desktop tools for invoices, reports, exports, reconciliation, and back-office tasks that need speed and reliability.",
  },
  {
    badge: "03",
    title: "Local data entry and sync",
    description:
      "Capture data locally, validate it on the machine, and sync it with a server or web platform when the connection is available.",
  },
];

const developmentSteps = [
  {
    label: "Discovery",
    title: "Map the workflow",
    description:
      "We understand who uses the software, what data moves through it, and where the current process slows down.",
  },
  {
    label: "Design",
    title: "Plan screens and data",
    description:
      "We shape the interface, permissions, database structure, and integration points before development starts.",
  },
  {
    label: "Build",
    title: "Develop the desktop app",
    description:
      "We implement the core app, local storage, validations, reports, integrations, and required admin controls.",
  },
  {
    label: "Test",
    title: "Verify real workflows",
    description:
      "We test installation, daily usage, edge cases, data handling, and the workflows your team depends on.",
  },
  {
    label: "Launch",
    title: "Deploy and support",
    description:
      "We prepare the release package, help with rollout, and support future updates as your process changes.",
  },
];

const relatedLinks = [
  {
    to: "/",
    kicker: "Home",
    title: "Back to QBITS9",
    description: "Return to the homepage and revisit the company story and core capabilities.",
    cta: "Open home",
  },
  {
    to: "/services/ai-automation",
    kicker: "Automation",
    title: "AI automation services",
    description: "Connect repetitive desktop workflows with smarter automation and integrated systems.",
    cta: "View service",
  },
  {
    to: "/services/web-mobile-development",
    kicker: "Product work",
    title: "Web & mobile apps",
    description: "Build connected web and mobile experiences around your desktop software.",
    cta: "View service",
  },
  {
    to: "/services/saas-erp-development",
    kicker: "Platform work",
    title: "SaaS & ERP development",
    description: "Create the cloud platform, ERP, or dashboard layer that supports your operations.",
    cta: "View service",
  },
  {
    to: "/contact",
    kicker: "CTA",
    title: "Start a desktop project",
    description: "Tell us what your team needs and we will help shape the software scope.",
    cta: "Contact us",
  },
];

const DesktopSoftware = () => (
  <ServicePageShell
    title="Desktop Software Development | QBITS9"
    description="QBITS9 builds custom desktop software, Windows applications, offline tools, and business desktop systems for companies in India."
    canonicalPath={pagePath}
    keywords="desktop software development India, custom desktop application, Windows desktop software, offline business software, desktop app modernization"
    schemas={schemas}
  >
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 top-[4%] md:w-[350px] w-[280px]">
        <img
          src={hero}
          alt="Desktop software development illustration"
          className="h-auto w-full"
          loading="eager"
          fetchPriority="high"
          decoding="async"
          width="800"
          height="800"
        />
      </div>
      <div className="mx-auto max-w-7xl px-5 py-16 md:py-24 lg:px-16">
        <nav className="flex flex-wrap items-center gap-2 lg:text-base text-sm font-[500] text-blue font-poppins lg:w-[65%] w-full px-5 lg:px-0">
          <Link to="/" className="hover:text-[#FF6B00]">
            Home
          </Link>
          <span>/</span>
          <span>Services</span>
          <span>/</span>
          <span className="text-slate-900">Desktop Software</span>
        </nav>

        <div className="mt-8 grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)] lg:items-center">
          <div className="space-y-8">
            <div className="space-y-5">
              <span className="font-peachi text-[16px] md:text-[18px] text-black font-[500] mb-3">
                Custom desktop tools for real business operations
              </span>
              <h1 className="text-black xl:text-[58px] lg:text-[50px] md:text-[42px] text-[28px] font-bold leading-[1.3]">
                Desktop software development for teams that need reliable systems.
              </h1>
              <p className="max-w-2xl text-lg leading-8 text-slate-600 font-poppins">
                We build custom desktop applications, offline tools, reporting utilities, and connected business software that helps teams work faster from their own machines.
              </p>
            </div>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/contact"
                className="w-fit relative overflow-hidden flex items-center justify-center bg-primary px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group cursor-pointer"
              >
                <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">
                  Start Desktop Project
                </span>
                <span className="absolute inset-1/2 h-0 w-0 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#E85D00] transition-all duration-500 group-hover:h-[25rem] group-hover:w-[25rem]" />
              </Link>
              <Link
                to="/contact"
                className="w-fit relative overflow-hidden flex items-center justify-center bg-blue px-6 sm:px-8 lg:px-[29px] py-3 lg:py-[15px] text-sm lg:text-base rounded-full group"
              >
                <span className="relative z-10 text-white font-unbounded text-sm lg:text-base whitespace-nowrap">
                  Get a Free Audit
                </span>
                <span className="absolute inset-1/2 w-0 h-0 bg-secondary rounded-full transition-all duration-700 group-hover:w-[25rem] group-hover:h-[25rem] -translate-x-1/2 -translate-y-1/2" />
              </Link>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              {heroStats.map((stat) => (
                <article
                  key={stat.title}
                  className="h-full bg-white rounded-[22px] py-[18px] px-[18px] flex flex-col gap-2 shadow-[0_12px_30px_0_#0000000F] hover:shadow-[0_12px_30px_0_#00000030] transition duration-500 ease-in-out"
                >
                  <p className="text-[18px] font-bold text-primary">{stat.title}</p>
                  <p className="text-[15px] font-light text-lightBlack line-clamp-3">{stat.value}</p>
                </article>
              ))}
            </div>
          </div>

          <div className="rounded-[40px] border border-slate-100 bg-white p-6 shadow-[0_16px_45px_rgba(0,0,0,0.08)]">
            <img
              className="w-full rounded-[32px]"
              src={techStackImg}
              alt="Desktop software architecture and technology stack"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </div>
    </section>

    <SectionWrap
      eyebrow="Services"
      title="Desktop software built around daily work"
      description="A desktop app is useful when speed, local workflows, hardware access, or offline use matter more than a browser-only experience."
      image={servicesBreakdownImg}
      imageAlt="Desktop software service breakdown"
    >
      <CardGrid items={services} columns="md:grid-cols-2" />
    </SectionWrap>

    <SectionWrap
      eyebrow="Use cases"
      title="Where desktop software usually helps fastest"
      description="We focus on practical systems that remove repeated manual work and make important business data easier to control."
      image={developmentProcessImg}
      imageAlt="Desktop software workflow planning"
      imagePosition="left"
    >
      <CardGrid items={useCases} columns="md:grid-cols-1" />
    </SectionWrap>

    <section className="py-16 md:py-20 bg-[#FFF7EF]">
      <div className="mx-auto max-w-7xl px-5 lg:px-16">
        <div className="max-w-3xl">
          <h3 className="font-peachi text-[28px] font-bold text-black mb-4">
            <span className="text-primary">Process</span>
          </h3>
          <h2 className="text-3xl font-semibold leading-tight text-slate-900 md:text-4xl font-unbounded">
            From workflow mapping to rollout
          </h2>
          <p className="mt-4 text-base leading-8 text-slate-600 font-poppins">
            The build stays focused on the people using the software every day, the data it manages, and the release path your business can maintain.
          </p>
        </div>
        <div className="mt-10">
          <Timeline steps={developmentSteps} columns="md:grid-cols-2 xl:grid-cols-5" />
        </div>
      </div>
    </section>

    <SectionWrap
      eyebrow="FAQ"
      title="Questions about desktop software projects"
      description="A few quick answers before you plan a desktop application or modernization project."
      image={techStackImg}
      imageAlt="Desktop software FAQ"
    >
      <FaqList faqs={faqs} />
    </SectionWrap>

    <section className="py-16 md:py-20 bg-[#FFF7EF]">
      <div className="mx-auto max-w-5xl px-5 lg:px-16">
        <div className="rounded-[30px] border border-[#FF6B0020] bg-white p-6 md:p-10 shadow-[0_20px_50px_rgba(15,23,42,0.06)]">
          <ServiceInquiryForm
            serviceName="Desktop Software"
            defaultSource="Desktop Software page"
            sourceOptions={[
              "Desktop Software page",
              "AI Automation page",
              "Web & Mobile Development page",
              "SaaS & ERP Development page",
            ]}
            submitLabel="Send desktop software enquiry"
            successTitle="Your desktop software enquiry is in"
          />
        </div>
      </div>
    </section>

    <RelatedServices
      links={relatedLinks}
      title="Explore connected services"
      description="Desktop software often works best when it connects with automation, web apps, or ERP systems."
      variant="service"
    />
  </ServicePageShell>
);

export default DesktopSoftware;
